/**
 * Enables nested Routes by appending /* to the path.
 * Use this for routes that contain nested <Routes> components.
 *
 * For empty or root paths, returns '*' (catchall) since React Router v6
 * nested routes receive the remaining path without a leading slash.
 */
export function enableNestedRoutes(path: string): string {
  if (path === '' || path === '/') return '*';
  return `${path}/*`;
}

/**
 * Creates a helper function for converting absolute paths to relative paths
 * for a specific base path. Use this in components with nested Routes.
 *
 * @example
 * const toRelative = createRelativeRouter('/workflows');
 * <Route path={toRelative(routes.WorkflowList)} /> // '/workflows/list' → 'list'
 */
export function createRelativeRouter(
  basePath: string,
  options: {
    nestedRoutes?: boolean;
  } = {}
) {
  const { nestedRoutes = false } = options;
  return {
    toRelativePath: (absolutePath: string): string => {
      if (!absolutePath.startsWith(basePath)) {
        throw new Error(
          `Route mismatch: "${absolutePath}" does not start with base "${basePath}". ` +
            `This likely means the route is in the wrong Routes component.`
        );
      }

      const relative = absolutePath.slice(basePath.length);
      const returnPath = relative.startsWith('/') ? relative.slice(1) : relative || '/';

      return nestedRoutes ? enableNestedRoutes(returnPath) : returnPath;
    },
  };
}
